import { makeEnvironmentProviders } from '@angular/core';
import { injectApiUrl } from '@seatix/shared-config';
import { createNoopInjectionToken } from 'ngxtension/create-injection-token';
import { VersionInterceptorConfig } from './version-config.interface';

/**
 * Injection token for the version header name
 */
export const [
  injectVersionHeaderName,
  provideVersionHeaderName,
  VERSION_HEADER_NAME,
] = createNoopInjectionToken<string>('Version Header Name');

/**
 * Injection token for the allowed URLs array
 */
export const [
  injectVersionHeaderAllowedUrls,
  provideVersionHeaderAllowedUrls,
  VERSION_HEADER_ALLOWED_URLS,
] = createNoopInjectionToken<string[]>('Version Header Allowed URLs');

/**
 * Creates an array of allowed URLs for API requests
 * @param apiUrl The base API URL to include
 * @returns Array containing the API URL
 */
export function createApiOnlyUrls(apiUrl: string): string[] {
  return [apiUrl];
}

/**
 * Creates an array of allowed URLs excluding certain patterns
 * @param baseUrls Array of base URLs to include
 * @param excludePatterns Array of URL patterns to exclude from the base URLs
 * @returns Array of allowed URLs without excluded patterns
 */
export function createAllowedUrlsWithExclusions(
  baseUrls: string[],
  excludePatterns: string[],
): string[] {
  return baseUrls.filter(
    (url) => !excludePatterns.some((pattern) => url.includes(pattern)),
  );
}

/**
 * Checks if a URL is allowed based on the allowedUrls array
 * @param url The URL to check
 * @param allowedUrls Array of allowed URL patterns
 * @returns True if the URL starts with any of the allowed URLs
 */
export function isUrlAllowed(url: string, allowedUrls: string[]): boolean {
  if (!allowedUrls || allowedUrls.length === 0) {
    return true;
  }
  return allowedUrls.some((allowedUrl) => url?.startsWith(allowedUrl) ?? false);
}

/**
 * Provides a complete version interceptor configuration
 * @param config Version interceptor configuration
 * @returns Array of providers
 */
export function provideVersionInterceptorConfig(
  config: VersionInterceptorConfig,
) {
  return makeEnvironmentProviders([
    provideVersionHeaderName(config.headerName),
    {
      provide: VERSION_HEADER_ALLOWED_URLS,
      useFactory: () => {
        const apiUrl = injectApiUrl();

        return config.allowedUrls ?? [apiUrl];
      },
    },
  ]);
}
